import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { resetPasswordWithToken } from '@/lib/auth';

export default function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Missing or invalid reset token. Please request a new password reset link.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    const res = await resetPasswordWithToken(token, newPassword, confirmPassword);
    setLoading(false);

    if (res.success) {
      setSuccess(res.message || 'Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/admin/login');
      }, 2500);
    } else {
      setError(res.error || 'Failed to reset password. Token may have expired.');
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 text-white flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-gradient-to-br from-neon-600 to-neon-400 text-white text-2xl font-bold mb-4 shadow-[0_0_30px_rgba(139,92,246,0.4)]">
            FC
          </div>
          <h1 className="font-display text-3xl font-bold text-gradient">Set New Password</h1>
          <p className="mt-2 text-sm text-white/50">FormaCode Security Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 sm:p-8 space-y-5 border border-white/10 shadow-2xl">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-start gap-2">
              <i className="ri-error-warning-line text-lg shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-lg shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-white/70 mb-2">New Password *</label>
            <div className="relative">
              <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                placeholder="Enter new password (min 6 chars)"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1 transition"
              >
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white/70 mb-2">Confirm New Password *</label>
            <div className="relative">
              <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Updating Password...
              </>
            ) : (
              <>
                Save New Password
                <i className="ri-check-double-line" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/admin/login" className="text-sm text-white/50 hover:text-neon-300 transition inline-flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back to Admin Login
          </Link>
        </div>
      </div>
    </div>
  );
}
